import {useCallback} from "react";

import {isUserDefaultActivityPage} from "@repo/guide-core-helpers";
import {GuideStateApi} from "@repo/guide-state";
import {AIActivity, UserPersona} from "@repo/guide-types";
import {Errors, KAError} from "@repo/logging";
import {useUserAttributes} from "@repo/shared-core";
import {useLocation} from "@repo/urls";

import {useResetConversation} from "../hooks/use-reset-conversation.ts";

import {getDefaultPersonaForUser} from "./get-default-persona-for-profile.ts";

interface ChangeStudentEmulationForUserFn {
    /**
     * Set whether the teacher should be emulating a student.
     *
     * @param emulateStudent Whether the teacher should be emulating a student.
     */
    (emulateStudent: boolean): void;
}

/**
 * Hook to obtain a function for changing student emulation for teacher or parent users.
 */
export const useChangeStudentEmulationForUser =
    (): ChangeStudentEmulationForUserFn => {
        const {pathname} = useLocation();
        const userAttributes = useUserAttributes();

        const resetConversation = useResetConversation();

        const setUserAsStudent = useCallback(
            (emulateStudent: boolean) => {
                // The user must be either a parent or teacher to make this call.
                const defaultPersona = getDefaultPersonaForUser(userAttributes);
                if (defaultPersona === UserPersona.Student) {
                    throw new KAError(
                        "Cannot set user as student for learner users",
                        Errors.Internal,
                    );
                }

                // If the user is already in the given emulation state, then
                // we don't need to do anything.
                if (GuideStateApi.latest().userAsStudent === emulateStudent) {
                    return;
                }

                // As soon as the user uses the asStudent toggle, we set this
                // flag so we can adjust our messaging and prevent restoring the
                // previous conversation thread.
                GuideStateApi.set("userHasToggledPersona", true);
                GuideStateApi.set("userAsStudent", emulateStudent);
                if (isUserDefaultActivityPage(pathname, defaultPersona)) {
                    GuideStateApi.set(
                        "selectedBotName",
                        emulateStudent
                            ? AIActivity.DefaultLearner
                            : defaultPersona === UserPersona.Teacher
                              ? AIActivity.DefaultTeacher
                              : AIActivity.DefaultParent,
                    );
                }
                resetConversation(true);
            },
            [pathname, userAttributes, resetConversation],
        );

        return setUserAsStudent;
    };
